/**
 * Delivery-issue arrival-greeting receipts — per-user, ANNOUNCED-only (no
 * ACKNOWLEDGED map — a delivery issue clears from the badge/mark by being
 * RESOLVED server-side, dismissed or resent, never by a local "seen" stamp).
 * Same adapter shape as `../admin/ownerTransferNoticeStorage.js` (read that
 * file first): per-user localStorage map, write-through to the server via
 * `upsertNoticeReceipts`, monotonic merge-from-server, no shared `:anon`
 * bucket — a missing userId reads/writes nothing rather than falling back to
 * a shared key.
 *
 * `announceDeliveryIssues` takes an ARRAY of ids (not one event_id at a
 * time, unlike the owner-transfer store) because the greeting modal can show
 * several open issues at once and "Got it" / "View invoices" / Later all
 * announce every row on screen in one shot.
 */
import { upsertNoticeReceipts } from '../../services/noticeReceipts';
import { isNewerIso, maxIso } from '../notices/noticeReceiptMath';
import { tenantIdForNoticeWrite } from '../notices/noticeReceiptsSession';

const DELIVERY_ISSUE_PREFIX = 'delivery_issue:';
// NO age prune (audit 2026-09-26, class C). The source — OPEN
// `document_delivery_issues` — has no age bound: an issue stays open until
// someone dismisses or resends it. The old 30-day prune-on-read dropped the
// announce stamp of an issue still open a month later, so it re-greeted (and
// kept re-greeting every 30 days). A resolved issue never reopens (a new
// bounce is a new row id), so a stale entry is inert; bound by COUNT instead,
// newest kept — far above any realistic number of simultaneously-open issues.
export const DELIVERY_ISSUE_MAX_ENTRIES = 1000;

function capNewest(map) {
  const ids = Object.keys(map);
  if (ids.length <= DELIVERY_ISSUE_MAX_ENTRIES) return map;
  const keep = ids
    .sort((a, b) => Date.parse(map[b]) - Date.parse(map[a]))
    .slice(0, DELIVERY_ISSUE_MAX_ENTRIES);
  const out = {};
  keep.forEach((id) => { out[id] = map[id]; });
  return out;
}

export function deliveryIssueAnnouncedKey(userId) {
  return `cvx-delivery-issue-announced-v1:${userId}`;
}

/** `{ issue_id: isoInstant }`. No user id → no bucket, ever (never `:anon`).
 *  `_nowMs` is kept for call-site compatibility (there is no age prune). */
function readAnnouncedMap(userId, storage, _nowMs) {
  if (!userId) return {};
  try {
    const raw = (storage || window.localStorage).getItem(deliveryIssueAnnouncedKey(userId));
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) return {};
    const out = {};
    Object.keys(parsed).forEach((id) => {
      const at = Date.parse(parsed[id]);
      if (!id || Number.isNaN(at)) return;
      out[id] = new Date(at).toISOString();
    });
    return capNewest(out);
  } catch {
    return {};
  }
}

export function readDeliveryIssueAnnounced(userId, storage, nowMs = Date.now()) {
  return readAnnouncedMap(userId, storage, nowMs);
}

/** Best-effort write (a storage failure is swallowed, never thrown). */
function writeAnnouncedMap(userId, next, storage) {
  if (!userId) return;
  try {
    (storage || window.localStorage).setItem(deliveryIssueAnnouncedKey(userId), JSON.stringify(capNewest(next)));
  } catch {
    /* best-effort — local cache only, the read side already fails soft */
  }
}

function syncDeliveryIssueReceipts(userId, ids, announced) {
  const tenantId = tenantIdForNoticeWrite(userId);
  if (!tenantId || !Array.isArray(ids) || ids.length === 0) return;
  upsertNoticeReceipts({
    tenantId,
    userId,
    rows: ids
      .filter((id) => id != null)
      .map((id) => ({
        notice_key: `${DELIVERY_ISSUE_PREFIX}${id}`,
        announced_at: announced[id] || null,
      })),
  });
}

/**
 * Marks every id in `ids` announced (idempotent per id) and returns the
 * RESULTING map, so a caller can `setState` directly from it — same shape as
 * every other store in this feature.
 */
export function announceDeliveryIssues(userId, ids, storage, nowMs = Date.now(), opts = {}) {
  const current = readAnnouncedMap(userId, storage, nowMs);
  if (!userId || !Array.isArray(ids) || ids.length === 0) return current;
  const stamp = new Date(nowMs).toISOString();
  const next = { ...current };
  // max, never `=`: a device with a lagging clock must not move a stamp back.
  ids.forEach((id) => { if (id != null) next[id] = maxIso(next[id], stamp); });
  writeAnnouncedMap(userId, next, storage);
  if (!opts.skipSync) syncDeliveryIssueReceipts(userId, ids, next);
  return next;
}

export function ownsDeliveryIssueNoticeKey(noticeKey) {
  return typeof noticeKey === 'string' && noticeKey.startsWith(DELIVERY_ISSUE_PREFIX);
}

/** Server rows → the local map, MONOTONIC (never move a timestamp backwards). */
export function mergeDeliveryIssueReceiptsFromServer(userId, rows, storage, nowMs = Date.now()) {
  if (!userId || !Array.isArray(rows)) return;
  rows.forEach((row) => {
    if (!ownsDeliveryIssueNoticeKey(row?.notice_key) || !row.announced_at) return;
    const issueId = row.notice_key.slice(DELIVERY_ISSUE_PREFIX.length);
    if (!issueId) return;
    const current = readAnnouncedMap(userId, storage, nowMs)[issueId];
    if (!isNewerIso(row.announced_at, current)) return;
    const merged = { ...readAnnouncedMap(userId, storage, nowMs), [issueId]: row.announced_at };
    writeAnnouncedMap(userId, merged, storage);
  });
}

/** Snapshot for the one-time post-hydration upload. */
export function collectDeliveryIssueReceiptsForUpload(userId, storage) {
  if (!userId) return [];
  const announced = readAnnouncedMap(userId, storage, Date.now());
  return Object.keys(announced).map((id) => ({
    notice_key: `${DELIVERY_ISSUE_PREFIX}${id}`,
    announced_at: announced[id] || null,
  }));
}
